import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { Hammer, MoreVertical, Search } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  FilterChip,
  Input,
  Skeleton,
  useToast,
} from '@/components/ui';
import { useMeAgents as useAgents } from '../../hooks/useAgents';
import { useAgentRoles } from '../../hooks/useAgentRoles';
import { useMeSpaces } from '../../hooks/useMeSpaces';
import { useCreateConversation } from '../../hooks/useConversations';
import { isPlatformEntity, type Agent } from '../../types';
import type { SpaceKind } from '../../types/spaces';
import { AgentCard } from '../../components/AgentCard';
import { routes } from '@/lib/routes';

const DEFAULT_PLATFORM_AGENT_NAME = 'Operator';
const PLATFORM_GROUP_KEY = '__platform__';
const RECOMMENDED_GROUP_KEY = '__recommended__';

function isRecommended(agent: Agent) {
  return isPlatformEntity(agent) && agent.name === DEFAULT_PLATFORM_AGENT_NAME;
}

type SortDir = 'asc' | 'desc';
type GroupBy = 'type' | 'space';

interface AgentGroup {
  key: string;
  label: string;
  agents: Agent[];
}

function sortByName(list: Agent[], dir: SortDir) {
  const sorted = [...list].sort(
    (a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
      (a.spaceName ?? '').localeCompare(b.spaceName ?? '', undefined, {
        sensitivity: 'base',
      }),
  );
  return dir === 'asc' ? sorted : sorted.reverse();
}

// Pin "Operator" first when present; everything else follows the sort.
function pinOperatorFirst(list: Agent[], dir: SortDir) {
  const operator = list.find((a) => a.name === DEFAULT_PLATFORM_AGENT_NAME);
  const rest = sortByName(
    list.filter((a) => a.name !== DEFAULT_PLATFORM_AGENT_NAME),
    dir,
  );
  return operator ? [operator, ...rest] : rest;
}

function matchesQuery(agent: Agent, q: string) {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    agent.name.toLowerCase().includes(needle) ||
    (agent.description ?? '').toLowerCase().includes(needle)
  );
}

type TypeBucket = 'operator' | 'coordinator' | 'agent' | 'sub-agent';

function classifyType(
  agent: Agent,
  roles: Map<string, Set<'coordinator' | 'sub-agent' | 'standalone'>>,
): TypeBucket {
  if (isPlatformEntity(agent)) return 'operator';
  const r = roles.get(agent.agentId);
  // Coordinator wins when an agent is both a coordinator and referenced as a
  // sub-agent — same precedence AgentList uses for badge display.
  if (r?.has('coordinator')) return 'coordinator';
  if (r?.has('sub-agent')) return 'sub-agent';
  return 'agent';
}

export default function NewChatPage() {
  const navigate = useNavigate();
  const { error: showError } = useToast();
  const { data, isLoading } = useAgents();
  const { data: spacesData } = useMeSpaces();
  const createConversation = useCreateConversation();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [groupBy, setGroupBy] = useState<GroupBy>('type');
  const [showSubAgents, setShowSubAgents] = useState(false);
  const [showBackgroundAgents, setShowBackgroundAgents] = useState(false);
  const [spaceFilter, setSpaceFilter] = useState<string>('all');

  // `Esc` focuses the agent search. Routed via react-hotkeys-hook so it shows
  // up in the Shift+? cheatsheet and inherits its built-in skip for
  // input/textarea/contentEditable targets.
  useHotkeys(
    'escape',
    (event) => {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus agent search',
      metadata: { group: 'New Chat' },
    },
  );

  const activeAgents = useMemo(
    () => (data?.items ?? []).filter((a) => a.status === 'active'),
    [data],
  );

  const roles = useAgentRoles(activeAgents);

  const spacesById = useMemo(() => {
    const map = new Map<string, { name: string; kind: SpaceKind }>();
    for (const s of spacesData?.items ?? []) {
      map.set(s.spaceId, { name: s.name, kind: s.kind });
    }
    return map;
  }, [spacesData]);

  // Space chips for the filter bar — only spaces that actually own agents in
  // the list, sorted personal-first then alphabetically (matches Activity /
  // SpaceSwitcher). Platform agents have no space and are excluded here.
  const spaceChips = useMemo(() => {
    const seen = new Map<string, { spaceId: string; label: string; kind: SpaceKind }>();
    for (const a of activeAgents) {
      if (isPlatformEntity(a) || !a.spaceId || seen.has(a.spaceId)) continue;
      const meta = spacesById.get(a.spaceId);
      seen.set(a.spaceId, {
        spaceId: a.spaceId,
        label: a.spaceName ?? meta?.name ?? 'Other',
        kind: meta?.kind ?? 'shared',
      });
    }
    return [...seen.values()].sort((x, y) => {
      const px = x.kind === 'personal' ? 0 : 1;
      const py = y.kind === 'personal' ? 0 : 1;
      return (
        px - py || x.label.localeCompare(y.label, undefined, { sensitivity: 'base' })
      );
    });
  }, [activeAgents, spacesById]);

  // Resolve the badge label for a card. Platform agents (Operator, Agent
  // Architect) belong to no space, so they get no badge.
  const spaceLabelFor = (a: Agent): string | undefined =>
    isPlatformEntity(a)
      ? undefined
      : a.spaceName ?? (a.spaceId ? spacesById.get(a.spaceId)?.name : undefined);

  const filteredAgents = useMemo(
    () =>
      activeAgents.filter((a) => {
        if (!matchesQuery(a, search)) return false;
        if (!showSubAgents && classifyType(a, roles) === 'sub-agent') return false;
        if (!showBackgroundAgents && !a.enableAui) return false;
        // Space filter narrows space-owned agents only; platform agents
        // (Operator / Agent Architect) stay visible across all spaces.
        if (spaceFilter !== 'all' && !isPlatformEntity(a) && a.spaceId !== spaceFilter)
          return false;
        return true;
      }),
    [activeAgents, search, showSubAgents, showBackgroundAgents, roles, spaceFilter],
  );

  const groups = useMemo<AgentGroup[]>(() => {
    const recommended = filteredAgents.filter(isRecommended);
    const rest = filteredAgents.filter((a) => !isRecommended(a));

    const recommendedGroup: AgentGroup | null =
      recommended.length > 0
        ? {
            key: RECOMMENDED_GROUP_KEY,
            label: 'Recommended',
            agents: sortByName(recommended, sortDir),
          }
        : null;

    if (groupBy === 'type') {
      const buckets: Record<TypeBucket, Agent[]> = {
        operator: [],
        coordinator: [],
        agent: [],
        'sub-agent': [],
      };
      for (const a of rest) {
        buckets[classifyType(a, roles)].push(a);
      }
      const orderedSections: { key: TypeBucket; label: string }[] = [
        { key: 'operator', label: 'Operator' },
        { key: 'coordinator', label: 'Coordinator' },
        { key: 'agent', label: 'Agent' },
        { key: 'sub-agent', label: 'Sub-agent' },
      ];
      const typeGroups = orderedSections
        .map((s) => ({
          key: s.key,
          label: s.label,
          agents:
            s.key === 'operator'
              ? pinOperatorFirst(buckets[s.key], sortDir)
              : sortByName(buckets[s.key], sortDir),
        }))
        .filter((g) => g.agents.length > 0);
      return recommendedGroup ? [recommendedGroup, ...typeGroups] : typeGroups;
    }

    // groupBy === 'space'
    const byKey = new Map<string, Agent[]>();
    for (const a of rest) {
      const key = isPlatformEntity(a)
        ? PLATFORM_GROUP_KEY
        : a.spaceId ?? '__unknown__';
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key)!.push(a);
    }

    const platform = byKey.get(PLATFORM_GROUP_KEY) ?? [];
    byKey.delete(PLATFORM_GROUP_KEY);

    const spaceGroups: AgentGroup[] = [...byKey.entries()]
      .map(([spaceId, agents]) => ({
        key: spaceId,
        label:
          agents[0]?.spaceName ?? spacesById.get(spaceId)?.name ?? 'Other',
        agents: sortByName(agents, sortDir),
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      );

    const out: AgentGroup[] = [];
    if (recommendedGroup) out.push(recommendedGroup);
    if (platform.length > 0) {
      out.push({
        key: PLATFORM_GROUP_KEY,
        label: 'Platform',
        agents: pinOperatorFirst(platform, sortDir),
      });
    }
    out.push(...spaceGroups);
    return out;
  }, [filteredAgents, groupBy, roles, sortDir, spacesById]);

  const handleSelect = async (agentId: string) => {
    try {
      const conversation = await createConversation.mutateAsync({
        agentId,
        persistent: true,
        origin: 'user',
      });
      navigate(routes.operator.chat(conversation.conversationId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to start chat');
    }
  };

  const noResults =
    !isLoading && search.trim() !== '' && groups.length === 0;
  const noAgents =
    !isLoading && search.trim() === '' && activeAgents.length === 0;

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-foreground">New Chat</h1>
        <Button onClick={() => navigate(routes.operator.builder)}>
          <Hammer className="mr-1 h-4 w-4" />
          Build Agents
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            value={search}
            onChange={(e: { target: { value: string } }) =>
              setSearch(e.target.value)
            }
            placeholder="Search"
            className="pl-9"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="View options"
              className="flex h-10 w-10 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Group by</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={groupBy}
              onValueChange={(v) => setGroupBy(v as GroupBy)}
            >
              <DropdownMenuRadioItem value="type">Type</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="space">Space</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Sort by name</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={sortDir}
              onValueChange={(v) => setSortDir(v as SortDir)}
            >
              <DropdownMenuRadioItem value="asc">A → Z</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="desc">Z → A</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={showSubAgents}
              onCheckedChange={setShowSubAgents}
            >
              Show sub-agents
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={showBackgroundAgents}
              onCheckedChange={setShowBackgroundAgents}
            >
              Show background agents
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {spaceChips.length >= 2 && (
        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip
            active={spaceFilter === 'all'}
            onClick={() => setSpaceFilter('all')}
          >
            All spaces
          </FilterChip>
          {spaceChips.map((s) => (
            <FilterChip
              key={s.spaceId}
              active={spaceFilter === s.spaceId}
              onClick={() => setSpaceFilter(s.spaceId)}
            >
              {s.label}
            </FilterChip>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      ) : (
        groups.map((g) => (
          <section key={g.key} className="space-y-3">
            <h2 className="text-sm text-muted-foreground">{g.label}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {g.agents.map((agent) => (
                <AgentCard
                  key={agent.agentId}
                  agent={agent}
                  spaceLabel={spaceLabelFor(agent)}
                  onSelect={handleSelect}
                  disabled={createConversation.isPending}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {noResults && (
        <p className="text-sm text-muted-foreground">
          No agents match “{search}”.
        </p>
      )}

      {noAgents && (
        <p className="text-sm text-muted-foreground">
          No agents available yet.
        </p>
      )}
    </div>
  );
}
